import { useUser } from '@/services/user';
import type { GetNotifications200ResponseMeta, Notification } from '@teemill/dashboard-user';
import { useNotifications, useNotificationsStore } from '@teemill/modules/notifications';
import { useLatestRequest } from '@/utils/useLatestRequest';
import { computed, ref, type Ref } from 'vue';

const PAGE_SIZE = 50;

export const useUserNotifications = (options?: {
  search: Ref<string | undefined>;
  sortBy: Ref<string[] | undefined>;
}) => {
  const user = useUser();
  const { decrementUnreadCount, incrementUnreadCount } = useNotificationsStore();

  const notifications = ref<Notification[]>([]);
  const meta = ref<GetNotifications200ResponseMeta | null>(null);
  const nextCursor = ref<string | null>(null);
  const isLoading = ref(false);
  const request = useLatestRequest();

  const fetchNotifications = async (cursor?: string) => {
    if (isLoading.value) {
      return;
    }

    const attempt = request.track();

    isLoading.value = true;

    try {
      const response = await user.notifications.list({
        search: options?.search?.value,
        sortBy: options?.sortBy?.value,
        cursor,
        limit: PAGE_SIZE,
      });

      if (attempt.isStale) return;

      notifications.value.push(...response.notifications);

      // Breakdown meta to add sorting (user first and then alphabetical)
      meta.value = {
        ...response.meta,
        filters: {
          ...response.meta.filters,
          groups: [...(response.meta.filters?.groups ?? [])].sort((a, b) => {
            if (a === 'user') return -1;
            if (b === 'user') return 1;
            return a.localeCompare(b);
          }),
        },
      };

      nextCursor.value = response.meta.pagination?.nextCursor ?? null;
    } catch {
      if (attempt.isStale) return;
      useNotifications().nudge('Failed to load notifications', { level: 'danger' });
    } finally {
      if (attempt.isCurrent) {
        isLoading.value = false;
      }
    }
  };

  const loadMore = async () => {
    if (!nextCursor.value) {
      return;
    }

    await fetchNotifications(nextCursor.value);
  };

  const reset = () => {
    request.invalidate();
    notifications.value = [];
    nextCursor.value = null;
    isLoading.value = false;
    fetchNotifications();
  };

  const markAsRead = async (id: string) => {
    const existingNotification = notifications.value.find((notification) => notification.id === id);

    if (!existingNotification || existingNotification.readAt) {
      return;
    }

    notifications.value = notifications.value.map((notification) => {
      if (notification.id === id) {
        return { ...notification, readAt: new Date().toISOString() };
      }
      return notification;
    });
    decrementUnreadCount();

    try {
      await user.notifications.read(id);
    } catch {
      // Silently fail on read errors
    }
  };

  const bulkMarkAs = async (ids: string[], read: boolean) => {
    const idSet = new Set(ids);
    const changedNotifications = notifications.value.filter((notification) => {
      if (!idSet.has(notification.id)) {
        return false;
      }

      return read ? !notification.readAt : !!notification.readAt;
    });

    notifications.value = notifications.value.map((notification) => {
      if (idSet.has(notification.id)) {
        return { ...notification, readAt: read ? new Date().toISOString() : null };
      }
      return notification;
    });
    if (read) {
      decrementUnreadCount(changedNotifications.length);
    } else {
      incrementUnreadCount(changedNotifications.length);
    }

    try {
      await user.notifications.bulkRead({
        notifications: ids.map((id) => ({ id, read })),
      });
    } catch {
      // Silently fail on read errors
    }
  };

  const bulkMarkAsRead = async (ids: string[]) => {
    await bulkMarkAs(ids, true);
  };

  const bulkMarkAsUnread = async (ids: string[]) => {
    await bulkMarkAs(ids, false);
  };

  const deleteNotification = async (id: string) => {
    await bulkDeleteNotifications([id]);
  };

  const bulkDeleteNotifications = async (ids: string[]) => {
    const idSet = new Set(ids);
    const deletedNotifications = notifications.value.filter((notification) => idSet.has(notification.id));
    const deletedUnreadCount = deletedNotifications.filter((notification) => !notification.readAt).length;

    notifications.value = notifications.value.filter((notification) => !idSet.has(notification.id));

    if (deletedUnreadCount) {
      decrementUnreadCount(deletedUnreadCount);
    }

    try {
      await Promise.all(ids.map((id) => user.notifications.delete(id)));
    } catch {
      // Silently fail on delete errors
    }
  };


  return {
    notifications,
    meta,
    isLoading,
    hasMore: computed(() => !!nextCursor.value),
    loadMore,
    reset,
    fetchNotifications,
    markAsRead,
    bulkMarkAsRead,
    bulkMarkAsUnread,
    deleteNotification,
    bulkDeleteNotifications,
  };
};
